import { Dialog, MenuItem, Switch } from "@mui/material";
import Button from "components/common/Button";
import Input from "components/common/Input";
import Select from "components/common/Select";
import api from "config/api";
import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { TimeItem } from "utils/types";

type Props = {
  options: any;
  selected?: TimeItem;
  onClose: (v?: boolean) => void;
};

const TimeSettingDialog = ({ options, onClose, selected }: Props) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<TimeItem | undefined>(undefined);
  const [confirmCopyAllOpen, setConfirmCopyAllOpen] = useState(false);

  useEffect(() => {
    if (selected) {
      setData({ ...selected, copyAll: false });
    }
  }, [selected]);

  const submitTiming = () => {
    if (!data) return;

    // Cloning data to avoid mutating state directly
    const { copyAll, ...rest } = structuredClone(data);

    // Date calculation logic
    const updateDateField = (dateString: string) => {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? new Date() : date;
    };

    const fromDate = updateDateField(rest.timFrom);
    const toDate = updateDateField(rest.timTo);
    toDate.setDate(fromDate.getDate() + +(+toDate < +fromDate));
    rest.timTo = format(new Date(toDate), "MM/dd/yyyy hh:mm:ss a");

    const updateOptionalRange = (fromKey: "timFrom1" | "timFrom2", toKey: "timTo1" | "timTo2") => {
      if (!rest[fromKey] || !rest[toKey]) {
        rest[fromKey] = "";
        rest[toKey] = "";
        return;
      }

      const fromDate = updateDateField(rest[fromKey]);
      const toDate = updateDateField(rest[toKey]);
      toDate.setDate(fromDate.getDate() + +(+toDate < +fromDate));
      rest[toKey] = format(new Date(toDate), "MM/dd/yyyy hh:mm:ss a");
    };

    updateOptionalRange("timFrom1", "timTo1");
    updateOptionalRange("timFrom2", "timTo2");

    setLoading(true);
    let promise: any;
    if (copyAll) {
      promise = Promise.all(
        options[data.timOrdtype].map((i: any) =>
          api.post("/api/RestTime/AddResttime", { ...rest, timDay: i.timDay })
        )
      );
    } else {
      promise = api.post("/api/RestTime/AddResttime", rest);
    }

    promise
      .then(() => {
        toast.success("Timing updated successfully");
        onClose(true);
      })
      .catch(() => toast.error("Something went wrong!"))
      .finally(() => setLoading(false));
  };

  const handleSubmit = () => {
    if (!data) return;

    if (data.copyAll) {
      setConfirmCopyAllOpen(true);
      return;
    }

    submitTiming();
  };

  if (!data) return <></>;

  const isTimingEnabled = !!data.timEnable;

  const formatTimeInputValue = (value?: string) => {
    if (!value) return "";
    const date = new Date(value);
    return isNaN(date.getTime()) ? "" : format(date, "HH:mm");
  };

  const updateTimeField = (
    key: "timFrom" | "timTo" | "timFrom1" | "timTo1" | "timFrom2" | "timTo2",
    value: string
  ) => {
    if (!value) {
      setData((e: any) => ({ ...e, [key]: "" }));
      return;
    }

    const [h, m] = value.split(":");
    const currentValue = data[key];
    const date = currentValue ? new Date(currentValue) : new Date(data.timFrom || new Date());
    date.setHours(Number(h));
    date.setMinutes(Number(m));
    setData((e: any) => ({
      ...e,
      [key]: format(date, "MM/dd/yyyy hh:mm:ss a"),
    }));
  };

  const clearTimeRange = (range: 1 | 2) => {
    setData((e: any) => ({
      ...e,
      ...(range === 1
        ? { timFrom1: "", timTo1: "" }
        : { timFrom2: "", timTo2: "" }),
    }));
  };

  return (
    <>
    <Dialog
      onClose={() => onClose()}
      open={!!selected}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: "!rounded-xl" }}
    >
      <div className="max-h-[90vh] flex flex-col overflow-hidden bg-white">
        <div className="px-5 pt-5 pb-4 border-b">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Restaurant Hours
          </p>
          <h4 className="!text-xl text-primary font-semibold">
            Update Timing
          </h4>
        </div>

        <div className="px-5 py-4 text-[15px] flex-1 flex flex-col gap-4 overflow-y-auto">
          <div
            className={`rounded-lg border px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${
              isTimingEnabled
                ? "border-green-200 bg-green-50"
                : "border-red-200 bg-red-50"
            }`}
          >
            <div>
              <p className="text-sm font-semibold text-gray-900">
                Timing Status
              </p>
              <p className="text-xs text-gray-600">
                {isTimingEnabled
                  ? "Customers can place orders for this day."
                  : "This day is disabled for online ordering."}
              </p>
            </div>
            <div className="flex items-center gap-2 self-start sm:self-center">
              <span
                className={`text-xs font-semibold ${
                  isTimingEnabled ? "text-green-700" : "text-red-700"
                }`}
              >
                {isTimingEnabled ? "Enabled" : "Disabled"}
              </span>
              <Switch
                checked={isTimingEnabled}
                onChange={() =>
                  setData((e: any) => ({
                    ...e,
                    timEnable: +!e.timEnable,
                  }))
                }
                color="success"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select
              label="Day"
              value={data.timDay}
              onValueChange={(timDay) =>
                setData((e: any) => ({ ...e, timDay }))
              }
            >
              {options[data.timOrdtype].map((item: any) => (
                <MenuItem key={item.timDay} value={item.timDay}>
                  {item.timDay}
                </MenuItem>
              ))}
            </Select>
            <Select
              label="Order Type"
              value={data.timOrdtype}
              onValueChange={(timOrdtype) =>
                setData((e: any) => ({ ...e, timOrdtype }))
              }
            >
              {Object.keys(options).map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              label="From"
              type="time"
              fullWidth
              value={formatTimeInputValue(data.timFrom)}
              onValueChange={(value) => updateTimeField("timFrom", value)}
            />
            <Input
              label="To"
              type="time"
              fullWidth
              value={formatTimeInputValue(data.timTo)}
              onValueChange={(value) => updateTimeField("timTo", value)}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-center">
            <Input
              label="From 1"
              type="time"
              fullWidth
              value={formatTimeInputValue(data.timFrom1)}
              onValueChange={(value) => updateTimeField("timFrom1", value)}
            />
            <Input
              label="To 1"
              type="time"
              fullWidth
              value={formatTimeInputValue(data.timTo1)}
              onValueChange={(value) => updateTimeField("timTo1", value)}
            />
            <button
              type="button"
              className="h-9 rounded-lg border border-gray-200 px-3 text-xs font-semibold text-gray-600 transition hover:border-red-200 hover:bg-red-50 hover:text-primary"
              onClick={() => clearTimeRange(1)}
            >
              Clear
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-center">
            <Input
              label="From 2"
              type="time"
              fullWidth
              value={formatTimeInputValue(data.timFrom2)}
              onValueChange={(value) => updateTimeField("timFrom2", value)}
            />
            <Input
              label="To 2"
              type="time"
              fullWidth
              value={formatTimeInputValue(data.timTo2)}
              onValueChange={(value) => updateTimeField("timTo2", value)}
            />
            <button
              type="button"
              className="h-9 rounded-lg border border-gray-200 px-3 text-xs font-semibold text-gray-600 transition hover:border-red-200 hover:bg-red-50 hover:text-primary"
              onClick={() => clearTimeRange(2)}
            >
              Clear
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-lg border border-gray-200 px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  Open 24hr
                </p>
                <p className="text-xs text-gray-500">Ignore time ranges.</p>
              </div>
              <Switch
                checked={!!data.timIs24Hr}
                onChange={() =>
                  setData((e: any) => ({
                    ...e,
                    timIs24Hr: +!e.timIs24Hr,
                  }))
                }
                color="success"
              />
            </div>
            <div className="rounded-lg border border-gray-200 px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-900">Copy All</p>
                <p className="text-xs text-gray-500">
                  Apply to every day in this order type.
                </p>
              </div>
              <Switch
                checked={!!data.copyAll}
                onChange={() =>
                  setData((e: any) => ({
                    ...e,
                    copyAll: !e.copyAll,
                  }))
                }
                color="success"
              />
            </div>
          </div>
        </div>

        <div className="border-t px-5 py-3">
          <Button className="w-full" disabled={loading} onClick={handleSubmit}>
            Update
          </Button>
        </div>
      </div>
    </Dialog>
      <Dialog
        open={confirmCopyAllOpen}
        onClose={() => !loading && setConfirmCopyAllOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ className: "!rounded-xl" }}
      >
        <div className="p-5">
          <h4 className="!text-lg font-semibold text-gray-900">
            Confirm Copy All
          </h4>
          <p className="mt-2 text-sm text-gray-600">
            This will apply the selected timing to every day for{" "}
            <span className="font-semibold text-gray-900">
              {data.timOrdtype}
            </span>
            . Are you sure you want to continue?
          </p>
          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 disabled:opacity-60"
              disabled={loading}
              onClick={() => setConfirmCopyAllOpen(false)}
            >
              Cancel
            </button>
            <Button
              className="px-4 py-2"
              disabled={loading}
              onClick={() => {
                setConfirmCopyAllOpen(false);
                submitTiming();
              }}
            >
              Yes, Copy All
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default TimeSettingDialog;
